import { ajax } from 'cadenza/api-client/api';
import type { UploadedFileDto } from 'cadenza/api-client/dto/uploaded-file-dto';
import {
  HEADER_DISY_UPLOAD_TYPE,
  HEADER_EXPORT_TYPE,
  MIMETYPE_DISY_SELFSERVICE_LAYER,
  MIMETYPE_DISY_WORKORDER_ID,
  MIMETYPE_JSON,
  PARAMETER_SRS_CODE
} from 'cadenza/api-client/api-constants';
import type { WorkOrderId } from 'cadenza/api-client/workorder-api';
import type { CapabilityKey } from 'cadenza/selfservice/import-data/api/capability-keys';
import type { ImportPresetDto } from 'cadenza/selfservice/import-data/api/import-preset-dto';
import type { ImportPresetSummaryDto } from 'cadenza/selfservice/import-data/api/import-preset-summary-dto';
import type { PatchPresetRequestDto } from 'cadenza/selfservice/import-data/api/import-preset-request-dto';
import type { ObjecttypeId } from 'cadenza/repository/objecttype-id';
import type { WorkbookKey } from 'cadenza/workbook/api/workbook-key';
import {
  OL_DEFAULT_MAP_SRS
} from 'cadenza/acquisition/acquisition-layer-utils';

/*
 * Example – by Coordinate:
 *
 * SelfServiceApi.createSelfServiceLayer({
 *     mapId: '948e640d-241f-420a-94d3-87b439725216',
 *     fileName: 'a0b0c0d0e0',
 *     signature: '5m5n5b5v5c',
 *     dataType: 'office',
 *     configuration: {
 *       columns: [
 *         {"index":0, "name":"Id", "attributeType":"STRING", marked: true},
 *         {"index":4, "name":"Label", "attributeType":"DOUBLE", marked: true},
 *         {"index":4, "name":"Hochwert", "attributeType":"DOUBLE"},
 *         {"index":10, "name":"Rechtswert", "attributeType":"DOUBLE", marked: true},
 *         {"index":11, "name":"Other", "attributeType":"STRING"}
 *       ],
 *       parseConfiguration: {
 *         containsHeader: true,
 *         decimalSeparator: ',', // Only CSV
 *         delimiter: '.',        // Only CSV
 *         quote: '"',            // Only CSV
 *         charset: 'UTF-8'      // Only CSV
 *       },
 *       themeName: "My new Layer from Excel file",
 *       layerColumnIndex: 0,
 *       spatialReference: {
 *         type: SpatialReference.BY_COORDINATE,
 *         xColumnIndex: 4,
 *         yColumnIndex: 10
 *         srs: 'EPSG:1234'
 *       }
 *     }
 * })
 *
 * Example – by Address:
 *
 * SelfServiceApi.createSelfServiceLayer({
 *     mapId: '948e640d-241f-420a-94d3-87b439725216',
 *     fileName: 'a0b0c0d0e0',
 *     signature: '5m5n5b5v5c',
 *     dataType: 'office',
 *     configuration: {
 *       columns: [
 *         {"index":0, "name":"Id", "attributeType":"STRING", marked: true},
 *         {"index":3, "name":"Label", "attributeType":"DOUBLE"},
 *         {"index":4, "name":"Land", "attributeType":"DOUBLE"},
 *         {"index":5, "name":"Bundesland", "attributeType":"DOUBLE", marked: true},
 *         {"index":6, "name":"PLZ", "attributeType":"INT"},
 *         {"index":10, "name":"Ort", "attributeType":"STRING", marked: true},
 *         {"index":13, "name":"Ortsteilname", "attributeType":"STRING", marked: true},
 *         {"index":14, "name":"Strasse", "attributeType":"STRING"},
 *         {"index":15, "name":"Hausnummer", "attributeType":"INT"},
 *         {"index":17, "name":"Hausnummerzusatz", "attributeType":"STRING"}
 *       ],
 *       parseConfiguration: {
 *         containsHeader: true,
 *         decimalSeparator: ',', // Only CSV
 *         delimiter: '.',        // Only CSV
 *         quote: '"',            // Only CSV
 *         charset: 'UTF-8'      // Only CSV
 *       },
 *       themeName: "My new Layer from Excel file",
 *       labelColumnIndex: 3,
 *       spatialReference: {
 *         type: SpatialReference.BY_STRUCTURED_ADDRESS,
 *         countryColumnIndex: 4,
 *         federalColumnIndex: 5,
 *         zipCodeColumnIndex: 6,
 *         cityColumnIndex: 10,
 *         cityDistrictColumnIndex: 13,
 *         streetColumnIndex: 14,
 *         streetNumberColumnIndex: 15,
 *         streetNumberAdditionColumnIndex: 17
 *       }
 *     }
 * })
 */
export function createSelfServiceLayer ({ mapId, fileName, signature, dataType, configuration }:
{ mapId: string; fileName: string; signature: string; dataType: string; configuration: unknown }) {
  const data = {
    fileName,
    signature,
    dataType,
    configuration: JSON.stringify(configuration)
  };
  return ajax(`/maps/${mapId}/layers`, {
    type: 'POST',
    headers: { [HEADER_DISY_UPLOAD_TYPE]: MIMETYPE_DISY_SELFSERVICE_LAYER },
    data
  });
}

export function checkIfObjectTypeDataCanBeExported (objectTypeId: string, mimeType: string) {
  return ajax(`/objecttypes/${btoa(objectTypeId)}/data`, {
    method: 'OPTIONS',
    headers: { [HEADER_EXPORT_TYPE]: mimeType }
  });
}

export function exportObjectTypeData (objectTypeId: string, mimeType: string) {
  return ajax(`/objecttypes/${btoa(objectTypeId)}/data`,
    { headers: { [HEADER_EXPORT_TYPE]: mimeType, 'Accept': MIMETYPE_DISY_WORKORDER_ID } });
}

export function createObjecttypeBasedOnTemplate (
  templateObjecttype: ObjecttypeId,
  objecttypePrintName: string,
  workbookKey: WorkbookKey) {
  const urlParams = new URLSearchParams({
    templateObjecttype: window.btoa(templateObjecttype),
    objecttypePrintName,
    repositoryName: workbookKey.repositoryName,
    workbookId: workbookKey.id
  });
  return ajax<ObjecttypeId>(`/objecttypes?${urlParams}`, { type: 'POST' });
}

export function addDatasetForObjecttype (
  objecttypeId: ObjecttypeId,
  mapSrs: string,
  dataset: Dataset) {
  const urlParams = new URLSearchParams({
    [PARAMETER_SRS_CODE]: mapSrs ?? OL_DEFAULT_MAP_SRS
  });
  return ajax(`/objecttypes/${btoa(objecttypeId)}/data?${urlParams}`, {
    type: 'POST',
    contentType: MIMETYPE_JSON,
    data: JSON.stringify(dataset)
  });
}

export function updateDatasetForObjecttype (
  objecttypeId: ObjecttypeId,
  id: string,
  mapSrs: string,
  values: Dataset) {
  const urlParams = new URLSearchParams({
    [PARAMETER_SRS_CODE]: mapSrs ?? OL_DEFAULT_MAP_SRS
  });
  return ajax(`/objecttypes/${btoa(objecttypeId)}/data/${id.toString()}?${urlParams}`, {
    type: 'PUT',
    contentType: MIMETYPE_JSON,
    data: JSON.stringify(values)
  });
}

export function deleteDataForObjecttype (
  objecttypeId: ObjecttypeId,
  ids: string[]
) {
  return ajax(`/objecttypes/${btoa(objecttypeId)}/data`, {
    type: 'DELETE',
    contentType: MIMETYPE_JSON,
    data: JSON.stringify(ids)
  });
}

/**
 * A single dataset (row) of an objecttype, including all values from attribute relations right
 * after the affected attribute (e.g. geometry).
 * Property keys reflect the attribute's name while property values are the individual attribute
 * values.
 * Geometries are encoded as GeoJson string.
 */
export interface Dataset { [key: string]: number | string | null }

/**
 * Fetches the complete objecttype dataset for the given datset id and objecttype id.
 * It assumes that the given id is of type Long (as string) and the only key attribute of the
 * objecttype. Also, the objecttype must be a basic objecttype without associations, hierarchies.
 *
 * @param objecttypeId - ID of the basic objecttype
 * @param id - Long value of the key attribute
 * @param mapSrs
 * @return Returns the complete objecttype dataset for the given datset id and objecttype id,
 *   including all values from attribute relations right after the affected attribute (e.g. geometry)
 */
export function getDatasetOfObjecttype (
  objecttypeId: ObjecttypeId,
  id: string,
  mapSrs: string): Promise<Dataset> {
  const urlParams = new URLSearchParams({
    [PARAMETER_SRS_CODE]: mapSrs ?? OL_DEFAULT_MAP_SRS
  });
  return ajax(`/objecttypes/${btoa(objecttypeId)}/data/${id}?${urlParams}`, {
    type: 'GET'
  });
}

export function checkIfObjecttypeIsLocked (objecttypeId: string) {
  return ajax(`/objecttypes/${btoa(objecttypeId)}/locked`);
}

interface SrsResults {
  results: unknown[];
  more: boolean;
  areFavoriteSRSesConfigured: boolean;
}

/*
 * Example:
 *
 * api.searchSrs('Gauß', { page: 1, limit 50 });
 */
export function searchSrs (term: string, { page = 1, limit = 10, shouldOnlyLoadFavoriteSRSes }:
{ page?: number; limit?: number; shouldOnlyLoadFavoriteSRSes?: boolean } = {}) {
  return ajax<SrsResults>('/srs', {
    type: 'GET',
    data: { term, page, limit, shouldOnlyLoadFavoriteSRSes }
  });
}

export function getImportVariants () {
  return ajax('/selfservice/import/variants', {
    headers: {
      Accept: MIMETYPE_JSON
    }
  });
}

export function getImportMetadata ({ mediaType, files, settings, metadataTarget }:
{ mediaType: string; files: UploadedFileDto[]; settings: object; metadataTarget: CapabilityKey[] }) {
  return ajax('/selfservice/import/metadata', {
    type: 'POST',
    contentType: MIMETYPE_JSON,
    data: JSON.stringify({ // see SelfServiceImportMetadataRequestDto
      mediaType,
      files,
      settings,
      metadataTarget
    })
  });
}

interface SelfServiceImportRequest {
  mediaType: string;
  files: UploadedFileDto[];
  settings: object;
  signal: AbortSignal;
}

export function getPreviews ({ mediaType, files, settings }: SelfServiceImportRequest, signal: AbortSignal) {
  return ajax('/selfservice/import/preview', {
    type: 'POST',
    contentType: MIMETYPE_JSON,
    signal,
    data: JSON.stringify({ // see SelfServiceImportPreviewRequestDto
      mediaType,
      files,
      settings
    })
  });
}

interface SelfServiceImportRequestDto {
  mediaType: string;
  files: UploadedFileDto[];
  settings: object;
  workbookKey?: WorkbookKey;
}

interface SelfServiceImportRequest extends SelfServiceImportRequestDto {
  signal: AbortSignal;
}

export function startImport ({ mediaType, files, settings, workbookKey }: SelfServiceImportRequest): Promise<WorkOrderId> {
  return ajax('/selfservice/import', {
    type: 'POST',
    contentType: MIMETYPE_JSON,
    data: JSON.stringify({ // see SelfServiceImportRequestDto
      mediaType,
      files,
      settings,
      workbookKey
    })
  });
}

export function savePreset (importPresetDto: ImportPresetDto) {
  return ajax('/selfservice/presets', {
    type: 'POST',
    contentType: MIMETYPE_JSON,
    data: JSON.stringify(importPresetDto)
  });
}

export function patchPreset (patchPresetRequestDto: PatchPresetRequestDto) {
  const fileExtension = patchPresetRequestDto.fileExtension;
  const importPresetId = patchPresetRequestDto.importPresetId;
  return ajax(`/selfservice/presets/${fileExtension}/${importPresetId}`, {
    type: 'PATCH',
    contentType: MIMETYPE_JSON,
    data: JSON.stringify({
      printName: patchPresetRequestDto.printName,
      settings: patchPresetRequestDto.settings
    })
  });
}

export function listPresets (fileExtension: string): Promise<ImportPresetSummaryDto[]> {
  return ajax(`/selfservice/presets/${fileExtension}`, { type: 'GET' });
}

export function getPreset (fileExtension: string, importPresetId: string): Promise<ImportPresetDto> {
  return ajax(`/selfservice/presets/${fileExtension}/${importPresetId}`, { type: 'GET' });
}

export function deletePreset ({ fileExtension, importPresetId }: { fileExtension: string; importPresetId: string }) {
  return ajax(`/selfservice/presets/${fileExtension}/${importPresetId}`, {
    type: 'DELETE'
  });
}
